import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../features/auth/authSlice';
import { useGetEmpresasQuery, useDeleteEmpresaMutation } from '../features/empresas/api/empresasApiSlice';
import { Empresa } from '../features/empresas/types';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { EmpresaForm } from '../features/empresas/components/EmpresaForm';
import { selectIsAdmin } from '../features/auth/authSlice';
import { showToast } from '../components/ui/Toast';
import { Logger } from '../lib/utils';

export const EmpresasPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentPage = 1;
  const itemsPerPage = 10;
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  
  const { data, error, isLoading, refetch } = useGetEmpresasQuery({ 
    offset: (currentPage - 1) * itemsPerPage, 
    limit: itemsPerPage 
  });
  const user = useSelector(selectCurrentUser);
  const isSuperAdmin = user?.role === 'superadmin';
  
  // Verificar si el usuario es admin o superadmin
  const isAdmin = useSelector(selectIsAdmin);
  
  Logger.debug("Estado de permisos:", { isAdmin, isSuperAdmin, isModalOpen });
  
  // Query y Mutations de RTK Query
  const [deleteEmpresa, { isLoading: isDeleting }] = useDeleteEmpresaMutation();

  // Refetch automático si la data está vacía y no está cargando
  useEffect(() => {
    if (!isLoading && (!data || !data.empresas || data.empresas.length === 0)) {
      refetch();
    }
  }, [data, isLoading, refetch]);

  // Modal functions
  const handleOpenModal = () => {
    setEditMode('create');
    setSelectedEmpresa(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmpresa(null);
  };

  const handleSubmitSuccess = () => {
    handleCloseModal();
    refetch();
  };

  // Handlers para acciones de la tabla
  const handleEdit = (empresa: Empresa) => {
    Logger.debug("Editando empresa:", empresa);
    setEditMode('edit');
    setSelectedEmpresa(empresa);
    setIsModalOpen(true);
    Logger.debug("Estado de modal después de editar:", { isModalOpen: true, mode: 'edit', empresa });
  };

  const handleDelete = async (id: number) => {
    if (!isSuperAdmin) {
      showToast('Solo los superadministradores pueden eliminar empresas', 'warning');
      return;
    }
    
    if (confirm('¿Estás seguro de que deseas eliminar esta empresa?')) {
      try {
        await deleteEmpresa(id).unwrap();
        showToast('Empresa eliminada con éxito', 'success');
      } catch (err) {
        Logger.error('Error al eliminar empresa:', err);
        showToast('Error al eliminar empresa', 'error');
      }
    }
  };

  // Renderizado condicional basado en el estado de la carga
  if (isLoading) {
    return <div className="text-center py-10 text-foreground">Cargando empresas...</div>;
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md mb-4">
        <p>Error al cargar empresas: {(error as { data?: { message?: string } })?.data?.message || 'Ocurrió un error desconocido'}</p>
      </div>
    );
  }
  
  // Contenido a mostrar cuando no hay empresas
  if (!data || !data.empresas || data.empresas.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">Gestión de Empresas</h1>
          <div>
            {isSuperAdmin && (
              <button
                onClick={handleOpenModal}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nueva Empresa</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border text-center py-8">
          <p className="text-muted-foreground">No hay empresas registradas en el sistema.</p>
        </div>

        {/* Modal para crear/editar empresa cuando no hay empresas */}
        {isModalOpen && (
          <EmpresaForm
            mode={editMode}
            empresa={selectedEmpresa}
            onClose={handleCloseModal}
            onSubmitSuccess={handleSubmitSuccess}
          />
        )}
      </div>
    );
  }

  // Contenido a mostrar cuando hay empresas
  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Gestión de Empresas</h1>
        {isSuperAdmin && (
          <button
            onClick={handleOpenModal}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nueva Empresa</span>
          </button>
        )}
      </div>

      <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border">
        <p className="text-muted-foreground mb-4">
          {isAdmin
            ? "Aquí podrás administrar las empresas del sistema."
            : "Aquí puedes ver las empresas registradas."}
        </p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse table-auto">
            <thead>
              <tr className="bg-muted/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Descripción</th>
                {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Acciones</th>}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {data.empresas.map((empresa) => (
                <tr key={empresa.id} className="hover:bg-muted/20">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{empresa.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{empresa.nombre}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {empresa.descripcion || <span className="text-muted-foreground/70 italic">Sin descripción</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button 
                        onClick={() => handleEdit(empresa)} 
                        className="text-primary hover:text-primary/80 mr-4 inline-flex items-center"
                        disabled={isDeleting || !isSuperAdmin}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Editar
                      </button>
                      <button 
                        onClick={() => handleDelete(empresa.id)} 
                        className="text-destructive hover:text-destructive/80 inline-flex items-center"
                        disabled={isDeleting || !isSuperAdmin}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Eliminar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear/editar empresa cuando hay empresas */}
      {isModalOpen && (
        <EmpresaForm
          mode={editMode}
          empresa={selectedEmpresa}
          onClose={handleCloseModal}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
    </div>
  );
}; 